import React, { ReactElement, useState, useEffect } from "react";
import TopBar from "./TopBar";
import LeftNav from "./LeftNav";
import { User } from "../types/auth";
import styles from './LoggedInLayout.module.css';

interface ChildProps {
  user: User | null;
  setUser: (user: User | null) => void;
  selectedOrgID: number | null;
  token: string | null;
  onOrgChange?: (orgID: number) => void;
}

interface LoggedInLayoutProps extends ChildProps {
  children: ReactElement<ChildProps>;
}

const LoggedInLayout: React.FC<LoggedInLayoutProps> = ({ 
  user, 
  setUser, 
  selectedOrgID, 
  token,
  onOrgChange, 
  children 
}) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't check for leftNav class since it's in a different module
      if (showMobileMenu && !target.closest('.leftNav') && !target.closest(`.${styles.mobileMenuToggle}`)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMenu]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  return (
    <div className={styles.loggedInLayout}>
      <TopBar 
        user={user} 
        selectedOrgID={selectedOrgID} 
        onOrgChange={onOrgChange}
        token={token}
        setUser={setUser}
      />
      <div className={styles.mainContentWrapper}>
        {/* Regular desktop nav */}
        <LeftNav user={user} selectedOrgID={selectedOrgID} />
        
        {/* Mobile nav - pass "show" as className when menu is open */}
        {showMobileMenu && (
          <LeftNav 
            user={user} 
            selectedOrgID={selectedOrgID}
            className="show" 
          />
        )}
        
        <div className={styles.mainContent}>
          {React.cloneElement(children, { user, setUser, selectedOrgID, token, onOrgChange })}
        </div>
      </div>
      <div 
        className={`${styles.mobileMenuOverlay} ${showMobileMenu ? styles.mobileMenuOverlayShow : ''}`}
        onClick={() => setShowMobileMenu(false)}
      />
      <button 
        className={styles.mobileMenuToggle} 
        onClick={toggleMobileMenu}
        aria-label="Toggle navigation menu"
      >
        ☰
      </button>
    </div>
  );
};

export default LoggedInLayout; 